"use client";

import type { VenuePageCtaBanner } from "@punchup/data/models/venue_pages";
import { ExternalLink } from "lucide-react";
import { getTextColorForBackground } from "@punchup/utils/colorContrast";
import { useVenuePage } from "../../hooks/venuePages/useVenuePage";

interface VenueCtaBannerProps {
  venueSlug: string;
}

function parseBanner(raw: unknown): VenuePageCtaBanner | null {
  if (!raw || typeof raw !== "object") return null;
  const obj = raw as Record<string, unknown>;
  if (
    typeof obj.enabled !== "boolean" ||
    typeof obj.text !== "string" ||
    typeof obj.url !== "string"
  ) {
    return null;
  }
  return {
    enabled: obj.enabled,
    text: obj.text,
    url: obj.url,
    background_color:
      typeof obj.background_color === "string"
        ? obj.background_color
        : undefined,
  };
}

function isSafeUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    return parsed.protocol === "https:" || parsed.protocol === "http:";
  } catch {
    return false;
  }
}

export default function VenueCtaBanner({ venueSlug }: VenueCtaBannerProps) {
  const { venuePage } = useVenuePage({ slug: venueSlug });
  const ctaBanner = parseBanner(venuePage?.cta_banner);

  if (!ctaBanner?.enabled || !ctaBanner.text || !ctaBanner.url) return null;
  if (!isSafeUrl(ctaBanner.url)) return null;

  const bgColor = ctaBanner.background_color ?? "#6366f1";
  const textColor = getTextColorForBackground(bgColor);

  return (
    <a
      href={ctaBanner.url}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center justify-center gap-2 w-full px-4 py-3 text-sm md:text-base font-semibold transition-opacity hover:opacity-90"
      style={{
        backgroundColor: bgColor,
        color: textColor,
      }}
    >
      <span>{ctaBanner.text}</span>
      <ExternalLink className="h-4 w-4 shrink-0" />
    </a>
  );
}
